import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import logoSite from "../../../../assets/brand/logo_site.svg";
import { getSentencesStreamUrl, resolveShareUrl } from "../../../constants";
import DemoVideoPicker from "./DemoVideoPicker";
import { getDemoShareSrc, DEMO_SRC_EVENT } from "./demoSource";

/**
 * Variant K — "TikTok in a box" (investor concept demo). Two phones, both REAL:
 * LEFT = the actual shipping editor embedded via the same-origin /s/ share (like HomeEtEmbed on B),
 * sized to 390px so the app renders its true MOBILE layout (sticky checkbox/thumbnail/sentence
 * columns — the vertical storyboard). RIGHT = a concept feed UI playing REAL clips from the same
 * shared Film Reel: we fetch the share's NDJSON stream (X-Share-Token header, no auth) and play
 * the one presigned video, seeking each card to its clip's [start_in, end_in].
 * The feed PRODUCT is the vision (labeled as such); the media in it is real.
 */

const PINK = "#e61d50";
const F = "Outfit, sans-serif";
const ANTON = "'Anton', Outfit, sans-serif";
const MONO = "'JetBrains Mono', monospace";
const REDUCED = "@media (prefers-reduced-motion: reduce)";

// The demo's share source comes from demoSource (getDemoShareSrc): the Film Reel by default,
// or a library video the logged-in user picks via <DemoVideoPicker />. Both phones follow it.

// Phone screen is 288px wide; lay the app out at 390px (a real phone viewport, below the ET's
// 500px xs breakpoint) and scale down — the app then renders its genuine mobile layout.
const ET_VIEWPORT_W = 390;
const ET_SCALE = 288 / ET_VIEWPORT_W;

const injectOnce = () => {
  if (typeof document === "undefined" || document.getElementById("cc-ph-fonts")) return;
  const l = document.createElement("link");
  l.id = "cc-ph-fonts";
  l.rel = "stylesheet";
  l.href =
    "https://fonts.googleapis.com/css2?family=Anton&family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@600&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.id = "cc-ph-kf";
  s.textContent = [
    "@keyframes ccPhUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}",
    "@keyframes ccPhEq{from{transform:scaleY(.2)}to{transform:scaleY(1)}}",
    "@keyframes ccPhBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}",
  ].join("\n");
  document.head.appendChild(s);
};

const up = (delay = 0) => ({
  animation: "ccPhUp .6s cubic-bezier(.2,.7,.2,1) both", animationDelay: `${delay}s`,
  [REDUCED]: { animation: "none" },
});

type FeedClip = { id: number; text: string; start: number; end: number; thumb: string };

const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k` : `${n}`);

/* ---------- phone chrome ---------- */

const StatusBar: React.FC = () => (
  <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 28, display: "flex", alignItems: "center", justifyContent: "space-between", px: "20px", zIndex: 6, pointerEvents: "none" }}>
    <Typography sx={{ fontFamily: MONO, fontSize: "10px", fontWeight: 600, color: "rgba(255,255,255,0.92)", textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>9:41</Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "1.5px" }}>
        {[3, 5, 7].map((h) => (
          <Box key={h} sx={{ width: "2.5px", height: `${h}px`, borderRadius: "1px", background: "rgba(255,255,255,0.9)" }} />
        ))}
      </Box>
      <Box sx={{ width: 17, height: 8.5, borderRadius: "2.5px", border: "1px solid rgba(255,255,255,0.55)", p: "1.5px", display: "flex" }}>
        <Box sx={{ width: "70%", borderRadius: "1px", background: "rgba(255,255,255,0.9)" }} />
      </Box>
    </Box>
  </Box>
);

const PhoneShell: React.FC<{ glow: string; children?: React.ReactNode }> = ({ glow, children }) => (
  <Box sx={{
    p: "10px", borderRadius: "48px", flexShrink: 0,
    background: "linear-gradient(160deg,#2a2733,#141219)", border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 90px -30px ${glow}`,
  }}>
    <Box sx={{ position: "relative", width: 288, height: 610, borderRadius: "38px", overflow: "hidden", background: "#0c0b10" }}>
      {/* dynamic island */}
      <Box sx={{ position: "absolute", top: 7, left: "50%", ml: "-42px", width: 84, height: 22, borderRadius: "12px", background: "#000", border: "1px solid rgba(255,255,255,0.07)", zIndex: 7, pointerEvents: "none" }} />
      <StatusBar />
      {children}
      {/* home indicator */}
      <Box sx={{ position: "absolute", bottom: 6, left: "50%", ml: "-50px", width: 100, height: 4, borderRadius: "999px", background: "rgba(255,255,255,0.35)", zIndex: 7, pointerEvents: "none" }} />
    </Box>
  </Box>
);

/* ---------- the page ---------- */

const PhoneDemoPage: React.FC = () => {
  const [inView, setInView] = useState(false);
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());
  const shareToken = useMemo(() => shareSrc.replace(/^\/s\//, ""), [shareSrc]);
  const [etLoaded, setEtLoaded] = useState(false);
  const [clips, setClips] = useState<FeedClip[]>([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [feedErr, setFeedErr] = useState("");
  const [liked, setLiked] = useState<Set<number>>(new Set());
  const [muted, setMuted] = useState(true);
  const [toast, setToast] = useState("");
  const [etPicks, setEtPicks] = useState<number[]>([]); // clip ids picked in the LEFT phone's real editor, in pick order
  const [basket, setBasket] = useState<number[]>([]); // CherryPick basket built from the feed
  const [basketOpen, setBasketOpen] = useState(false);
  const [basketPlaying, setBasketPlaying] = useState(false);
  const [framePos, setFramePos] = useState<Record<number, { x: number; y: number }>>({}); // per-clip 9:16 focus point (objectPosition %)
  const [reframeId, setReframeId] = useState<number | null>(null); // clip currently in drag-to-reposition mode
  const dragFrom = useRef<{ x: number; y: number } | null>(null);

  const sectionRef = useRef<HTMLDivElement>(null);
  const etFrameRef = useRef<HTMLIFrameElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const cardEls = useRef<Map<number, HTMLDivElement>>(new Map());
  const videoEls = useRef<Map<number, HTMLVideoElement>>(new Map());
  const progressEls = useRef<Map<number, HTMLDivElement>>(new Map());
  const activeId = useRef<number | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  // useLayoutEffect so the ccPh* keyframes exist before first paint (no entrance flicker)
  useLayoutEffect(() => {
    injectOnce();
    const tHeld = toastTimer;
    return () => window.clearTimeout(tHeld.current);
  }, []);

  // lazy-load both phones once the section approaches the viewport (same pattern as HomeEtEmbed)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // fetch the REAL shared reel: resolve token -> stream NDJSON (share token header ONLY — never
  // the login Bearer, or the presigned URLs sign against the wrong bucket)
  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    setClips([]); setVideoUrl(""); setFeedErr("");
    (async () => {
      try {
        const r1 = await fetch(resolveShareUrl(shareToken));
        const b1 = await r1.json().catch(() => ({}));
        if (!r1.ok || !b1.valid) throw new Error("share-resolve");
        const r2 = await fetch(`${getSentencesStreamUrl}${b1.transcript_id}/`, { headers: { "X-Share-Token": shareToken } });
        if (!r2.ok) throw new Error("share-stream");
        const text = await r2.text();
        let video = "";
        const rows: FeedClip[] = [];
        text.split("\n").forEach((line) => {
          if (!line) return;
          let d: any;
          try { d = JSON.parse(line); } catch (e) { return; }
          if (!video && d.total_rows && d.video) { video = d.video; return; }
          if (d.type === "sentence" && d.data && !d.data.space) {
            rows.push({ id: d.data.id, text: d.data.sentence || "", start: d.data.start_in, end: d.data.end_in, thumb: d.data.thumbnail || "" });
          }
        });
        if (cancelled) return;
        if (!video || !rows.length) { setFeedErr("Couldn't load the sample reel."); return; }
        const good = rows.filter((r) => r.end - r.start >= 2);
        setVideoUrl(video);
        setClips((good.length >= 4 ? good : rows).slice(0, 12));
      } catch (e) {
        if (!cancelled) setFeedErr("Couldn't load the sample reel — try the full editor instead.");
      }
    })();
    return () => { cancelled = true; };
  }, [inView, shareToken]);

  // Repoint both phones when the demo video changes (picker or Letter B).
  useEffect(() => {
    const onSrc = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "string" && next) { setShareSrc(next); setEtLoaded(false); }
    };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  // watch the LEFT phone's selection (same-origin iframe): checked boxes -> ordered clip ids.
  // The mobile ET renders the pick-order ordinal (1st/2nd…) under each checked box, and each
  // row's <video> is id'd v-<clipId> — that's the DOM contract we read here.
  useEffect(() => {
    if (!inView) return;
    const t = window.setInterval(() => {
      try {
        const doc = etFrameRef.current?.contentDocument;
        if (!doc) return;
        const checked = doc.querySelectorAll("td.checkbox .Mui-checked");
        const picks: Array<{ id: number; ord: number }> = [];
        checked.forEach((el) => {
          const tr = el.closest("tr");
          const vid = tr?.querySelector('video[id^="v-"]');
          const id = parseInt((vid?.getAttribute("id") || "").slice(2), 10);
          if (Number.isNaN(id)) return;
          let ord = picks.length + 1;
          tr?.querySelectorAll("td.checkbox span").forEach((s) => {
            const m = (s.textContent || "").trim().match(/^(\d+)(st|nd|rd|th)$/);
            if (m) ord = parseInt(m[1], 10);
          });
          picks.push({ id, ord });
        });
        picks.sort((a, b) => a.ord - b.ord);
        const ids = picks.map((p) => p.id);
        setEtPicks((prev) => (prev.join(",") === ids.join(",") ? prev : ids));
      } catch (e) { /* iframe not ready */ }
    }, 900);
    return () => window.clearInterval(t);
  }, [inView]);

  // what the feed plays: basket reel > left-phone picks (as one stitched reel) > all clips
  const feedList = useMemo(() => {
    const byId = new Map(clips.map((c) => [c.id, c]));
    const playlist = (basketPlaying && basket.length ? basket : etPicks)
      .map((id) => byId.get(id))
      .filter(Boolean) as FeedClip[];
    return playlist.length ? playlist : clips;
  }, [clips, etPicks, basket, basketPlaying]);
  const inReelMode = feedList !== clips && feedList.length > 0;

  // when the reel changes (picks made/cleared, basket played), restart the feed from the top
  const reelKey = basketPlaying ? `b:${basket.join(",")}` : `p:${etPicks.join(",")}`;
  useEffect(() => {
    activeId.current = null;
    try { feedRef.current?.scrollTo({ top: 0 }); } catch (e) { /* ignore */ }
  }, [reelKey]);

  // one clip plays at a time: the card filling the feed viewport (seek to start, loop inside clip)
  useEffect(() => {
    const rootEl = feedRef.current;
    if (!rootEl || !feedList.length) return;
    const startClip = (v: HTMLVideoElement, clip: FeedClip) => {
      const go = () => {
        try {
          if (Math.abs(v.currentTime - clip.start) > 0.25) {
            v.addEventListener("seeked", () => { v.play().catch(() => { /* autoplay denied */ }); }, { once: true });
            v.currentTime = clip.start;
          } else {
            v.play().catch(() => { /* autoplay denied */ });
          }
        } catch (e) { /* ignore */ }
      };
      if (v.readyState >= 1) go();
      else {
        v.preload = "auto";
        v.addEventListener("loadedmetadata", go, { once: true });
        try { v.load(); } catch (e) { /* ignore */ }
      }
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        let id: number | null = null;
        cardEls.current.forEach((el, key) => { if (el === en.target) id = key; });
        if (id == null) return;
        const v = videoEls.current.get(id);
        if (!en.isIntersecting) {
          if (v) { try { v.pause(); } catch (e) { /* ignore */ } }
          if (activeId.current === id) activeId.current = null;
          return;
        }
        if (activeId.current === id) return;
        const prev = activeId.current != null ? videoEls.current.get(activeId.current) : null;
        if (prev) { try { prev.pause(); } catch (e) { /* ignore */ } }
        activeId.current = id;
        const clip = feedList.find((c) => c.id === id);
        if (v && clip) startClip(v, clip);
      });
    }, { root: rootEl, threshold: 0.6 });
    cardEls.current.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [feedList]);

  const say = (m: string) => {
    setToast(m);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 3400);
  };

  const like = (id: number) => setLiked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // React doesn't reliably propagate `muted` prop updates to the DOM — set it imperatively too
  const toggleMute = () => {
    const next = !muted;
    videoEls.current.forEach((v) => { v.muted = next; });
    setMuted(next);
  };

  const addToBasket = (id: number) => {
    setBasket((b) => {
      if (b.includes(id)) { say("Already in your CherryPick basket 🍒"); return b; }
      say(`CherryPicked! ${b.length + 1} clip${b.length ? "s" : ""} in your basket`);
      return [...b, id];
    });
  };

  /* ---------- left phone: the REAL editor (mobile layout via 390px viewport) ---------- */

  const editorPhone = (
    <Box sx={{ position: "absolute", top: 30, left: 0, right: 0, bottom: 0, background: "#0b0a0f" }}>
      {inView ? (
        <iframe
          ref={etFrameRef}
          title="CherryClipz — the real editor, mobile view"
          src={shareSrc}
          allow="fullscreen; clipboard-write"
          onLoad={() => setEtLoaded(true)}
          style={{
            position: "absolute", top: 0, left: 0,
            width: `${100 / ET_SCALE}%`, height: `${100 / ET_SCALE}%`,
            transform: `scale(${ET_SCALE})`, transformOrigin: "top left",
            border: "none", background: "#0b0a0f",
          }}
        />
      ) : null}
      {!etLoaded && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f" }}>
          <img src={logoSite} alt="" width={34} style={{ opacity: 0.85 }} />
          <Typography sx={{ fontFamily: F, fontSize: "12px", color: "#9aa0b4" }}>Loading the real editor…</Typography>
        </Box>
      )}
    </Box>
  );

  /* ---------- right phone: the feed, playing REAL clips from the same reel ---------- */

  const feedPhone = (
    <>
    <Box ref={feedRef} sx={{
      position: "absolute", inset: 0, overflowY: "auto", scrollSnapType: "y mandatory",
      scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" },
    }}>
      {!clips.length && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f", px: "24px", textAlign: "center" }}>
          <img src={logoSite} alt="" width={34} style={{ opacity: 0.85 }} />
          <Typography sx={{ fontFamily: F, fontSize: "12px", color: "#9aa0b4" }}>
            {feedErr || "Loading real clips from the reel…"}
          </Typography>
        </Box>
      )}
      {feedList.map((c, i) => (
        <Box
          key={c.id}
          ref={(el: HTMLDivElement | null) => { if (el) cardEls.current.set(c.id, el); else cardEls.current.delete(c.id); }}
          sx={{
            position: "relative", height: "100%", scrollSnapAlign: "start", overflow: "hidden",
            display: "flex", flexDirection: "column", justifyContent: "flex-end", p: "14px", pb: "26px",
            background: "#08070b",
          }}
        >
          {/* real clip video — object-fit cover = the 9:16 zoom-to-fill look of a real export */}
          <video
            ref={(el) => { if (el) videoEls.current.set(c.id, el); else videoEls.current.delete(c.id); }}
            src={videoUrl}
            poster={c.thumb || undefined}
            muted={muted}
            playsInline
            preload="none"
            onClick={(e) => {
              const v = e.currentTarget;
              if (v.paused) v.play().catch(() => { /* ignore */ });
              else v.pause();
            }}
            onTimeUpdate={(e) => {
              const v = e.currentTarget;
              if (v.currentTime >= c.end - 0.05) {
                if (inReelMode && feedList.length > 1) {
                  // stitched-reel preview: auto-advance to the next pick (wraps to the top)
                  const next = (i + 1) % feedList.length;
                  try { v.pause(); } catch (err) { /* ignore */ }
                  v.currentTime = c.start;
                  const h = feedRef.current?.clientHeight || 610;
                  try { feedRef.current?.scrollTo({ top: next * h, behavior: "smooth" }); } catch (err) { /* ignore */ }
                } else {
                  v.currentTime = c.start;
                }
              }
              const p = progressEls.current.get(c.id);
              if (p) {
                const pct = ((v.currentTime - c.start) / Math.max(0.1, c.end - c.start)) * 100;
                p.style.width = `${Math.min(100, Math.max(0, pct))}%`;
              }
            }}
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", cursor: "pointer",
              objectPosition: `${framePos[c.id]?.x ?? 50}% ${framePos[c.id]?.y ?? 50}%`,
            }}
          />
          {/* readability scrim */}
          <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(180deg, rgba(8,7,11,0.35) 0%, transparent 30%, transparent 55%, rgba(8,7,11,0.72) 100%)" }} />

          {/* clip progress */}
          <Box sx={{ position: "absolute", top: 32, left: 14, right: 14, height: 2, borderRadius: "999px", background: "rgba(255,255,255,0.16)", overflow: "hidden" }}>
            <Box ref={(el: HTMLDivElement | null) => { if (el) progressEls.current.set(c.id, el); else progressEls.current.delete(c.id); }}
              sx={{ height: "100%", width: 0, background: "rgba(255,255,255,0.75)" }} />
          </Box>

          {/* drag-to-reposition overlay: choose this clip's 9:16 focus point by dragging */}
          {reframeId === c.id && (
            <Box
              onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => {
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
                dragFrom.current = { x: e.clientX, y: e.clientY };
              }}
              onPointerMove={(e: React.PointerEvent<HTMLDivElement>) => {
                const d = dragFrom.current;
                if (!d) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const dx = ((e.clientX - d.x) / rect.width) * 100;
                const dy = ((e.clientY - d.y) / rect.height) * 100;
                dragFrom.current = { x: e.clientX, y: e.clientY };
                setFramePos((p) => {
                  const cur = p[c.id] || { x: 50, y: 50 };
                  return { ...p, [c.id]: { x: Math.min(100, Math.max(0, cur.x - dx)), y: Math.min(100, Math.max(0, cur.y - dy)) } };
                });
              }}
              onPointerUp={() => { dragFrom.current = null; }}
              sx={{ position: "absolute", inset: 0, zIndex: 4, touchAction: "none", cursor: "grab", "&:active": { cursor: "grabbing" }, border: `2px dashed ${PINK}aa` }}
            >
              <Typography sx={{
                position: "absolute", top: 44, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap",
                fontFamily: MONO, fontSize: "8.5px", letterSpacing: "0.5px", color: "#fff",
                background: "rgba(0,0,0,0.6)", borderRadius: "6px", px: "8px", py: "4px", pointerEvents: "none",
              }}>DRAG TO REPOSITION · TAP ✓ WHEN DONE</Typography>
            </Box>
          )}

          {/* right rail */}
          <Box sx={{ position: "absolute", right: 10, bottom: 92, display: "flex", flexDirection: "column", alignItems: "center", gap: "13px", zIndex: 5 }}>
            <Box sx={{ textAlign: "center" }}>
              <Box component="button" onClick={() => like(c.id)} sx={{
                cursor: "pointer", width: 38, height: 38, borderRadius: "999px", border: "1px solid rgba(255,255,255,0.16)",
                background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", p: 0,
                color: liked.has(c.id) ? PINK : "#fff", "&:hover": { borderColor: PINK },
              }}><FavoriteIcon sx={{ fontSize: 18 }} /></Box>
              <Typography sx={{ fontFamily: MONO, fontSize: "8.5px", color: "#fff", mt: "3px" }}>{fmt(64 + ((c.id * 37) % 900) + (liked.has(c.id) ? 1 : 0))}</Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ width: 38, height: 38, borderRadius: "999px", border: "1px solid rgba(255,255,255,0.16)", background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 17 }} />
              </Box>
              <Typography sx={{ fontFamily: MONO, fontSize: "8.5px", color: "#fff", mt: "3px" }}>{fmt(8 + ((c.id * 13) % 140))}</Typography>
            </Box>
            <Box sx={{ width: 38, height: 38, borderRadius: "999px", border: "1px solid rgba(255,255,255,0.16)", background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
              <SendIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box component="button" title="CherryPick: add this clip to your basket, then stitch your picks into a new reel"
                onClick={() => addToBasket(c.id)}
                sx={{
                  cursor: "pointer", width: 38, height: 38, borderRadius: "999px",
                  border: basket.includes(c.id) ? `1.5px solid ${PINK}` : `1px solid ${PINK}88`,
                  background: basket.includes(c.id) ? `${PINK}33` : "rgba(0,0,0,0.35)",
                  display: "flex", alignItems: "center", justifyContent: "center", p: 0,
                  "&:hover": { background: `${PINK}33` },
                }}><img src={logoSite} alt="CherryPick" width={17} style={{ display: "block" }} /></Box>
              <Typography sx={{ fontFamily: MONO, fontSize: "8.5px", color: "#fff", mt: "3px" }}>CherryPick</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box component="button" title="Reframe: drag the video to choose what the 9:16 crop shows"
                onClick={() => setReframeId((cur) => (cur === c.id ? null : c.id))}
                sx={{
                  cursor: "pointer", width: 38, height: 38, borderRadius: "999px",
                  border: reframeId === c.id ? `1.5px solid ${PINK}` : "1px solid rgba(255,255,255,0.16)",
                  background: reframeId === c.id ? `${PINK}33` : "rgba(0,0,0,0.35)",
                  display: "flex", alignItems: "center", justifyContent: "center", p: 0, color: "#fff",
                  "&:hover": { borderColor: PINK },
                }}>{reframeId === c.id ? <Typography sx={{ fontSize: "15px", lineHeight: 1 }}>✓</Typography> : <OpenWithIcon sx={{ fontSize: 17 }} />}</Box>
              <Typography sx={{ fontFamily: MONO, fontSize: "8.5px", color: "#fff", mt: "3px" }}>{reframeId === c.id ? "Done" : "Frame"}</Typography>
            </Box>
          </Box>

          {/* burned-caption block + handle */}
          <Box sx={{ maxWidth: 205, zIndex: 2 }}>
            <Typography sx={{
              fontFamily: F, fontWeight: 800, fontSize: "14.5px", lineHeight: 1.5, color: "#fff",
              display: "inline", background: "rgba(0,0,0,0.42)", borderRadius: "6px", px: "6px", py: "2px",
              boxDecorationBreak: "clone" as any, WebkitBoxDecorationBreak: "clone" as any,
            }}>{c.text}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "10px" }}>
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: 12 }}>
                {[0, 1, 2, 3].map((n) => (
                  <Box key={n} sx={{
                    width: 2.5, height: 12, borderRadius: "1px", background: "#fff", transformOrigin: "bottom",
                    animation: `ccPhEq ${0.7 + n * 0.13}s ease-in-out ${n * 0.1}s infinite alternate`,
                    [REDUCED]: { animation: "none", transform: "scaleY(0.6)" },
                  }} />
                ))}
              </Box>
              <Typography sx={{ fontFamily: F, fontWeight: 700, fontSize: "11px", color: "#fff" }}>@filmreel</Typography>
              <Typography sx={{ fontFamily: F, fontSize: "10px", color: "#d5d9e6" }}>· Made with 🍒 CherryClipz</Typography>
            </Box>
          </Box>

          {i === 0 && feedList.length > 1 && !inReelMode && (
            <Box sx={{
              position: "absolute", bottom: 14, left: "50%", ml: "-22px", width: 44, textAlign: "center",
              opacity: 0.6, pointerEvents: "none", animation: "ccPhBob 1.7s ease-in-out infinite",
              [REDUCED]: { animation: "none" },
            }}>
              <KeyboardArrowUpIcon sx={{ fontSize: 18, color: "#fff", display: "block", mx: "auto" }} />
              <Typography sx={{ fontFamily: MONO, fontSize: "8px", color: "#fff", mt: "-4px" }}>swipe</Typography>
            </Box>
          )}
        </Box>
      ))}

    </Box>
    {/* sound toggle (real audio from the reel) — overlays the screen, outside the scroller */}
    {clips.length > 0 && (
      <Box component="button" onClick={toggleMute} title={muted ? "Unmute" : "Mute"} sx={{
        position: "absolute", top: 36, right: 10, zIndex: 8,
        cursor: "pointer", width: 34, height: 34, borderRadius: "999px", border: "1px solid rgba(255,255,255,0.2)",
        background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: "14px", p: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        "&:hover": { borderColor: PINK },
      }}>{muted ? "🔇" : "🔊"}</Box>
    )}

    {/* reel-mode banner: the feed is playing a stitched preview, not the open feed */}
    {inReelMode && (
      <Box sx={{
        position: "absolute", top: 38, left: 12, zIndex: 8, maxWidth: 200,
        display: "flex", alignItems: "center", gap: "6px",
        fontFamily: MONO, fontSize: "8.5px", letterSpacing: "0.5px", color: "#fff",
        background: `${PINK}dd`, borderRadius: "6px", px: "7px", py: "4px",
      }}>
        {basketPlaying ? "BASKET REEL — YOUR CHERRYPICKS" : "YOUR PICKS — PLAYING AS ONE REEL"}
        {basketPlaying && (
          <Box component="button" onClick={() => setBasketPlaying(false)} title="Back to the feed"
            sx={{ cursor: "pointer", border: "none", background: "transparent", color: "#fff", fontSize: "10px", p: 0, lineHeight: 1 }}>✕</Box>
        )}
      </Box>
    )}

    {/* CherryPick basket bar */}
    {basket.length > 0 && !basketOpen && (
      <Box component="button" onClick={() => setBasketOpen(true)} sx={{
        position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", zIndex: 8,
        cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
        fontFamily: F, fontWeight: 700, fontSize: "11px", color: "#fff",
        background: "rgba(18,16,24,0.92)", border: `1px solid ${PINK}88`, borderRadius: "999px",
        px: "12px", py: "7px", whiteSpace: "nowrap", "&:hover": { background: `${PINK}33` },
      }}>
        <img src={logoSite} alt="" width={13} /> {basket.length} CherryPicked · arrange & play
      </Box>
    )}

    {/* CherryPick basket panel — arrange the order, play as one reel */}
    {basketOpen && (
      <Box sx={{ position: "absolute", inset: 0, zIndex: 9, background: "rgba(8,7,11,0.96)", display: "flex", flexDirection: "column", pt: "34px", pb: "16px", px: "12px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px", mb: "10px" }}>
          <img src={logoSite} alt="" width={16} />
          <Typography sx={{ fontFamily: F, fontWeight: 800, fontSize: "13px", color: "#f3f4f9", flex: 1 }}>Your CherryPick basket</Typography>
          <Box component="button" onClick={() => setBasketOpen(false)}
            sx={{ cursor: "pointer", border: "none", background: "transparent", color: "#9aa0b4", fontSize: "16px", p: 0, "&:hover": { color: "#fff" } }}>✕</Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          {basket.map((id, bi) => {
            const c = clips.find((x) => x.id === id);
            if (!c) return null;
            return (
              <Box key={id} sx={{ display: "flex", alignItems: "center", gap: "8px", py: "6px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <Typography sx={{ fontFamily: MONO, fontSize: "10px", color: PINK, width: 16 }}>{bi + 1}</Typography>
                <Box sx={{ flexShrink: 0, width: 52, height: 30, borderRadius: "5px", overflow: "hidden", background: "#15151c" }}>
                  {c.thumb ? <img src={c.thumb} alt="" width={52} height={30} style={{ objectFit: "cover", display: "block" }} /> : null}
                </Box>
                <Typography sx={{ fontFamily: F, fontSize: "10px", lineHeight: 1.3, color: "#d5d9e6", flex: 1 }}>
                  {c.text.length > 52 ? `${c.text.slice(0, 52)}…` : c.text}
                </Typography>
                <Box component="button" title="Move up"
                  onClick={() => setBasket((b) => { if (bi === 0) return b; const n = [...b]; [n[bi - 1], n[bi]] = [n[bi], n[bi - 1]]; return n; })}
                  sx={{ cursor: "pointer", border: "1px solid rgba(255,255,255,0.18)", background: "transparent", color: "#c2c7d6", borderRadius: "5px", width: 20, height: 20, p: 0, fontSize: "10px", "&:hover": { borderColor: PINK } }}>↑</Box>
                <Box component="button" title="Remove"
                  onClick={() => setBasket((b) => b.filter((x) => x !== id))}
                  sx={{ cursor: "pointer", border: "none", background: "transparent", color: "#6b7186", fontSize: "12px", p: 0, "&:hover": { color: PINK } }}>✕</Box>
              </Box>
            );
          })}
          {!basket.length && (
            <Typography sx={{ fontFamily: F, fontSize: "11px", color: "#9aa0b4", textAlign: "center", mt: "24px" }}>
              Basket's empty — tap the 🍒 button on any clip in the feed.
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: "8px", mt: "10px" }}>
          <Box component="button"
            onClick={() => { if (!basket.length) return; setBasketPlaying(true); setBasketOpen(false); }}
            sx={{
              cursor: "pointer", flex: 1, border: "none", borderRadius: "9px", py: "9px",
              fontFamily: F, fontWeight: 800, fontSize: "11.5px", color: "#fff",
              background: `linear-gradient(100deg,#ff4d7d,${PINK})`, opacity: basket.length ? 1 : 0.5,
              "&:hover": { filter: "brightness(1.08)" },
            }}>▶ Play as one reel</Box>
          <Box component="button"
            onClick={() => { setBasket([]); setBasketPlaying(false); }}
            sx={{
              cursor: "pointer", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "9px", px: "12px",
              fontFamily: F, fontWeight: 600, fontSize: "11px", color: "#c2c7d6", background: "transparent",
              "&:hover": { borderColor: PINK },
            }}>Clear</Box>
        </Box>
      </Box>
    )}
    </>
  );

  /* ---------- layout ---------- */

  const labelChip = (text: string, color: string) => (
    <Box sx={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "1.5px", color, border: `1px solid ${color}55`, borderRadius: "999px", px: "12px", py: "5px", mb: "16px" }}>
      {text}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: "16px", md: "28px" }, py: { xs: "28px", md: "52px" }, overflow: "hidden" }}>
      {/* hero */}
      <Box sx={{ textAlign: "center", mb: { xs: "36px", md: "56px" } }}>
        <Typography sx={{ ...up(0), fontFamily: MONO, fontSize: "11px", letterSpacing: "3px", color: PINK, mb: "14px" }}>
          CHERRYCLIPZ · INVESTOR CONCEPT DEMO
        </Typography>
        <Typography component="h1" sx={{ ...up(0.05), fontFamily: ANTON, fontSize: { xs: "46px", md: "88px" }, lineHeight: 0.98, color: "#f3f4f9", m: 0 }}>
          TikTok in a box.
        </Typography>
        <Typography sx={{ ...up(0.12), fontFamily: F, fontSize: { xs: "15px", md: "17px" }, lineHeight: 1.65, color: "#b9bfd1", maxWidth: 760, mx: "auto", mt: "18px" }}>
          Two phones. On the left, the real CherryClipz editor — live, not a mockup — a vertical storyboard
          of the whole video, clip after clip. On the right, the vision: where those clips would go next — a
          feed built from what users export, shown here playing real clips from the same reel.
        </Typography>
        <Typography sx={{ ...up(0.18), fontFamily: MONO, fontSize: "11.5px", letterSpacing: "1px", color: "#f5a524", mt: "18px" }}>
          ▶ TRY IT: PICK CLIPS ON THE LEFT — THE RIGHT PHONE PLAYS YOUR PICKS AS ONE REEL
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", mt: "18px" }}>
          <DemoVideoPicker />
        </Box>
      </Box>

      {/* phones */}
      <Box ref={sectionRef} sx={{ position: "relative", display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "center", md: "flex-start" }, justifyContent: "center", gap: { xs: "44px", md: "110px" } }}>
        <Box sx={{ ...up(0.2), display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 320 }}>
          {labelChip("① THE REAL EDITOR — LIVE, SHIPS TODAY", "#22c55e")}
          <PhoneShell glow={`${PINK}66`}>{editorPhone}</PhoneShell>
          <Typography sx={{ fontFamily: F, fontSize: "13px", lineHeight: 1.6, color: "#9aa0b4", textAlign: "center", mt: "18px" }}>
            The actual shipping editor on a sample reel, in its real mobile view: checkbox, thumbnail,
            transcript, timecodes. Tap a thumbnail to play, tick clips in your pick order, trim with − / +
            (0.1s a tap), and Export stitches your picks — 9:16 with burned captions included.
          </Typography>
        </Box>

        <Box sx={{ ...up(0.26), display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 320 }}>
          {labelChip("② THE FEED — THE VISION", "#f5a524")}
          <PhoneShell glow="#f5a52455">{feedPhone}</PhoneShell>
          <Typography sx={{ fontFamily: F, fontSize: "13px", lineHeight: 1.6, color: "#9aa0b4", textAlign: "center", mt: "18px" }}>
            Real clips from that same reel. Pick clips on the left and this phone plays them back-to-back —
            your stitch, previewed as a reel. Or tap 🍒 CherryPick on feed clips to fill a basket, arrange
            the order, and play <em>that</em> as one reel: remixing, the CherryClipz way.
          </Typography>
        </Box>
      </Box>

      {/* flywheel */}
      <Box sx={{ maxWidth: 980, mx: "auto", mt: { xs: "56px", md: "88px" }, textAlign: "center" }}>
        <Typography sx={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "3px", color: PINK, mb: "20px" }}>WHY THIS COMPOUNDS</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "10px" }}>
          {([
            { step: "Upload anything", vision: false },
            { step: "CherryPick the best moments", vision: false },
            { step: "Export 9:16 with captions", vision: false },
            { step: "Clips land in the feed", vision: true },
            { step: "Viewers become creators", vision: true },
          ] as const).map(({ step, vision }, i, arr) => (
            <React.Fragment key={step}>
              <Box sx={{
                fontFamily: F, fontWeight: 600, fontSize: "13.5px",
                color: vision ? "#f5d9a8" : "#e6e8f0",
                border: vision ? "1px dashed #f5a52466" : "1px solid rgba(255,255,255,0.14)",
                borderRadius: "999px", px: "16px", py: "9px", background: "rgba(255,255,255,0.03)",
              }}>
                {step}
              </Box>
              <Typography sx={{ fontFamily: MONO, fontSize: "14px", color: i === arr.length - 1 ? "#f5a524" : "#6b7186" }}>
                {i === arr.length - 1 ? "↺" : "→"}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
        <Typography sx={{ fontFamily: MONO, fontSize: "9.5px", letterSpacing: "1px", color: "#8a7a5c", mt: "10px" }}>
          SOLID = SHIPS TODAY · DASHED = THE VISION
        </Typography>
        <Typography sx={{ fontFamily: F, fontSize: { xs: "14.5px", md: "16px" }, lineHeight: 1.7, color: "#b9bfd1", maxWidth: 720, mx: "auto", mt: "24px" }}>
          Every social platform has the same cold-start problem: a feed with nothing in it. CherryClipz ships
          with its own supply chain — the editor. Every user who CherryPicks a sermon, a podcast, a lecture
          would be producing feed content for everyone else. That's the box: the studio and the network, together.
        </Typography>
      </Box>

      {/* CTAs */}
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "14px", mt: { xs: "36px", md: "52px" } }}>
        <Box component="button" onClick={() => { window.location.href = "/?layout=B"; }} sx={{
          cursor: "pointer", border: "none", borderRadius: "12px", px: "22px", py: "13px",
          fontFamily: F, fontWeight: 800, fontSize: "15px", color: "#fff",
          background: `linear-gradient(100deg,#ff4d7d,${PINK})`, "&:hover": { filter: "brightness(1.08)" },
        }}>Try the full-size editor →</Box>
        <Box component="button" onClick={() => { window.location.href = "/?layout=J"; }} sx={{
          cursor: "pointer", borderRadius: "12px", px: "22px", py: "13px",
          fontFamily: F, fontWeight: 700, fontSize: "15px", color: "#f3f4f9",
          border: "1px solid rgba(255,255,255,0.22)", background: "transparent", "&:hover": { borderColor: PINK },
        }}>Join the waitlist</Box>
      </Box>

      {/* honesty footnote */}
      <Typography sx={{ fontFamily: F, fontSize: "12px", lineHeight: 1.6, color: "#6b7186", textAlign: "center", maxWidth: 680, mx: "auto", mt: "36px" }}>
        Demo notes: the left phone is the real editor embedded live (the same shared sample reel as the
        full-size editor page) — nothing on it is mocked. The right phone plays real clips from that reel
        inside a concept feed UI; the feed product itself isn't built yet — it's what we're raising to
        build — and the engagement numbers are illustrative.
      </Typography>

      {toast && (
        <Box sx={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 13000,
          fontFamily: F, fontSize: "13px", fontWeight: 600, color: "#fff",
          background: "rgba(18,16,24,0.96)", border: `1px solid ${PINK}66`, borderRadius: "999px",
          px: "18px", py: "10px", boxShadow: "0 10px 34px rgba(0,0,0,0.55)", maxWidth: "88vw",
        }}>{toast}</Box>
      )}
    </Box>
  );
};

export default PhoneDemoPage;
